export type SubmissionPlanType = "residential_site_plan" | "lot_split" | "lot_combination";

export type SubmissionStatus = "received" | "in_review" | "awaiting_revision" | "completed";

export type Submission = {
  id: number;
  status: string;
  plan_type: SubmissionPlanType;
  applicant_name: string;
  applicant_email: string | null;
  project_name: string;
  description: string | null;
  desired_zoning: string | null;
  site_address: string | null;
  parcel_identifier: string | null;
  assigned_planner_id: number | null;
  assigned_planner_name: string | null;
  submitter_user_id: number | null;
  submitter_name: string | null;
  latest_plan_review_id: number | null;
  source_filename: string | null;
  municipality_id: number;
  created_at: string;
  updated_at: string;
};

export type AssignablePlanner = { id: number; email: string };

export type SubmissionSort = "newest" | "oldest" | "status" | "assignee";

export type SubmissionQueueFilters = {
  status?: SubmissionStatus;
  assigned_planner_id?: number | "unassigned";
  sort?: SubmissionSort;
  search?: string;
  page?: number;
  per_page?: number;
  signal?: AbortSignal;
};

export type SubmissionsQueueResponse = {
  submissions: Submission[];
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  next_page: number | null;
};

export type CreateSubmissionInput = {
  file: File;
  plan_type: SubmissionPlanType;
  applicant_name: string;
  project_name: string;
  applicant_email?: string;
  description?: string;
  desired_zoning?: string;
  site_address?: string;
  parcel_identifier?: string;
};

const OPTIONAL_FIELDS = [
  "applicant_email",
  "description",
  "desired_zoning",
  "site_address",
  "parcel_identifier",
] as const;

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

async function parseOrThrow(response: Response) {
  if (response.ok) return response.json();
  const data = await response.json().catch(() => ({}));
  throw new Error(data.errors?.join(", ") ?? data.error ?? `Request failed with ${response.status}`);
}

export async function createSubmission(
  municipalityId: number,
  input: CreateSubmissionInput,
): Promise<{ submission: Submission }> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("plan_type", input.plan_type);
  form.append("applicant_name", input.applicant_name.trim());
  form.append("project_name", input.project_name.trim());

  for (const field of OPTIONAL_FIELDS) {
    const value = input[field]?.trim();
    if (value) form.append(field, value);
  }

  const response = await fetch(`/api/v1/municipalities/${municipalityId}/submissions`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
    body: form,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? `Failed to create submission (${response.status})`);
  }

  return response.json();
}

export async function listSubmissions(
  municipalityId: number,
  filters: SubmissionQueueFilters = {},
): Promise<SubmissionsQueueResponse> {
  const query = new URLSearchParams();
  if (filters.status) query.set("status", filters.status);
  if (filters.assigned_planner_id != null) {
    query.set("assigned_planner_id", String(filters.assigned_planner_id));
  }
  if (filters.sort) query.set("sort", filters.sort);
  if (filters.search) query.set("search", filters.search);
  if (filters.page) query.set("page", String(filters.page));
  if (filters.per_page) query.set("per_page", String(filters.per_page));

  const qs = query.toString();
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/submissions${qs ? `?${qs}` : ""}`,
    { credentials: "include", signal: filters.signal },
  );

  return parseOrThrow(response);
}

export async function updateSubmission(
  municipalityId: number,
  id: number,
  patch: Partial<Pick<Submission, "status" | "assigned_planner_id">>,
): Promise<{ submission: Submission }> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/submissions/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ submission: patch }),
  });

  return parseOrThrow(response);
}

export async function runSubmissionReview(
  municipalityId: number,
  id: number,
  checklistId: number,
): Promise<{ plan_review: { id: number; status: string } }> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/submissions/${id}/run_review`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ checklist_id: checklistId }),
    },
  );

  return parseOrThrow(response);
}

export async function listAssignablePlanners(
  municipalityId: number,
  signal?: AbortSignal,
): Promise<AssignablePlanner[]> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/assignable_planners`,
    { credentials: "include", signal },
  );
  const data = await parseOrThrow(response);
  return data.planners;
}
